import type { Action } from "@reduxjs/toolkit";
import { setLang } from "../redux/features/lang/langSlice";
import { applyServerUi } from "../redux/features/uiSlice";
import { applyServerJobForm } from "../redux/features/jobFormSlice";
import { applyServerWeights } from "../redux/features/comparisonPreferencesSlice";

/**
 * Maps the server's winning data from a 409 user-state conflict into the
 * Redux actions that reconcile the *source* slices a synced payload is
 * derived from. Without this, a rejected sync only updates the sync-tracking
 * entry (userState.entries[itemKey]) and not the slices that generate the
 * outgoing payload, so the next sync attempt re-sends the same losing
 * payload forever — see useProfilePreferencesSettingsSync's debounced effect.
 *
 * Pure and side-effect free so the mapping can be unit tested without
 * rendering the hook (this repo has no renderHook/@testing-library setup).
 */

export function buildSettingsConflictActions(data: Record<string, unknown>): Action[] {
  const actions: Action[] = [];
  if (typeof data.lang === "string") {
    actions.push(setLang(data.lang));
  }
  if (data.ui && typeof data.ui === "object") {
    actions.push(applyServerUi(data.ui as Parameters<typeof applyServerUi>[0]));
  }
  return actions;
}

export function buildProfileConflictActions(data: Record<string, unknown>): Action[] {
  return [applyServerJobForm(data as Parameters<typeof applyServerJobForm>[0])];
}

export function buildPreferencesConflictActions(data: Record<string, unknown>): Action[] {
  return [applyServerWeights(data as Parameters<typeof applyServerWeights>[0])];
}
